import type { RouteResult } from "@flowsight/sim";
import { CANONICAL_ORIGIN } from "./config";

/** Share URL carries the clicked point (+ radius) so the link reproduces the
 *  result (A9 / SPEC v0.7 §5). Always uses the canonical domain so links are
 *  stable regardless of which host the app was opened from. No token/mediaUrl. */
export function buildShareUrl(from: { lat: number; lon: number }, radiusKm?: number): string {
  const u = new URL(CANONICAL_ORIGIN + "/");
  u.searchParams.set("from", `${from.lat.toFixed(5)},${from.lon.toFixed(5)}`);
  if (radiusKm != null) u.searchParams.set("r", String(radiusKm));
  return u.toString();
}

export function parseShareUrl(): { lat: number; lon: number; radiusKm?: number | undefined } | null {
  const q = new URLSearchParams(location.search);
  // accept ?from=lat,lon (current) or ?lat=&lon= (SPEC v0.7 form)
  let lat: number, lon: number;
  const raw = q.get("from");
  if (raw) {
    [lat, lon] = raw.split(",").map((s) => Number(s.trim())) as [number, number];
  } else {
    lat = Number(q.get("lat")); lon = Number(q.get("lon"));
  }
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const r = Number(q.get("r"));
  return { lat, lon, radiusKm: Number.isFinite(r) && r > 0 ? r : undefined };
}

/** Plain-language text ready to paste into LINE / chat. */
export function buildLineText(r: RouteResult, from: { lat: number; lon: number }, placeLabel: string | null = null, radiusKm?: number): string {
  const eta = `${fmtDays(r.etaDaysFast)}–${fmtDays(r.etaDaysSlow)} วัน`;
  const head = r.reachesGulf
    ? `น้ำจากจุดนี้ไหลลงอ่าวไทย 🌊`
    : `น้ำจากจุดนี้ไม่ไหลลงอ่าวไทย (ไหลลง${r.destinationName})`;
  const lines = [
    `💧 น้ำถึงไหน${placeLabel ? " · " + placeLabel : ""}`,
    head,
    r.reachesGulf ? `• ถึงปากอ่าวไทยในประมาณ ${eta}` : `• ระยะไหลถึงปลายทางประมาณ ${eta}`,
    `• ระยะทางตามลำน้ำ ~${Math.round(r.lengthKm)} กม.`,
    `• น้ำท่าเฉลี่ยแถวจุดนี้ ~${Math.round(r.qStartCms)} ลบ.ม./วิ`,
    `📞 ฉุกเฉิน: ปภ. 1784 · การแพทย์ 1669 · เหตุร้าย 191`,
    buildShareUrl(from, radiusKm),
  ];
  return lines.join("\n");
}

/** v0.9.5a C2: on a phone open LINE's own share sheet; on desktop copy the text to paste into LINE.
 *  Returns what happened so the button can say so. */
export async function shareToLine(text: string): Promise<"line" | "copied"> {
  const phone = window.matchMedia("(max-width: 767px)").matches || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (phone) {
    window.open(`https://line.me/R/share?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    return "line";
  }
  try { await navigator.clipboard.writeText(text); }
  catch { const ta = document.createElement("textarea"); ta.value = text; document.body.append(ta); ta.select(); document.execCommand("copy"); ta.remove(); }
  return "copied";
}

export function fmtDays(d: number): string {
  if (d < 1) return (Math.round(d * 10) / 10).toString();
  return Math.round(d).toString();
}
